/**
 * One appliance in the kitchen: a fridge, a freezer, or a pantry cupboard.
 *
 * Drawn rather than tiled, because a grid of three labelled rectangles is a
 * form and a fridge is a fridge. It is still the same line language as every
 * icon in the app — one stroke weight, `currentColor`, no fills except the
 * quiet surface behind the door — just at a size where it reads as an object.
 *
 * The door genuinely swings. `rotateY` about the hinge edge with a shallow
 * perspective is the cheapest thing that reads as opening rather than sliding,
 * and it collapses to a plain fade when the viewer asks for reduced motion.
 */

import type { StorageLocation } from "../features/food/types";
import type { FoodCategory } from "../features/food/types";

import "./Appliance.css";

interface ApplianceProps {
  place: StorageLocation;
  /** How many items are in there. */
  count: number;
  /**
   * Up to five categories present, drawn as marks on the shelves so an open
   * appliance shows something of what's actually inside.
   */
  contents: FoodCategory[];
  open: boolean;
  onToggle: (place: StorageLocation) => void;
}

const LABELS: Record<StorageLocation, string> = {
  fridge: "Fridge",
  freezer: "Freezer",
  pantry: "Pantry",
};

/** Where the shelves sit inside each body, as fractions of its height. */
const SHELVES: Record<StorageLocation, number[]> = {
  fridge: [42, 74, 106],
  freezer: [50, 90],
  pantry: [40, 70, 100, 130],
};

export function Appliance({ place, count, contents, open, onToggle }: ApplianceProps) {
  const shelves = SHELVES[place];
  // Spread whatever is inside across the shelves, at most two marks each, so a
  // full fridge suggests fullness rather than trying to inventory itself.
  const marks = contents.slice(0, shelves.length * 2);

  return (
    <button
      className="appliance"
      type="button"
      data-place={place}
      data-open={open || undefined}
      aria-expanded={open}
      aria-label={`${LABELS[place]}, ${count} ${count === 1 ? "item" : "items"}`}
      onClick={() => onToggle(place)}
    >
      <svg className="appliance__art" viewBox="0 0 100 170" aria-hidden="true">
        {/* The inside: shelves, and a mark per category that's in there. */}
        <g className="appliance__interior">
          <rect x="8" y="8" width="84" height="154" rx="9" />
          {shelves.map((y) => (
            <line key={y} x1="16" y1={y} x2="84" y2={y} />
          ))}
          {marks.map((category, index) => {
            const shelf = shelves[Math.floor(index / 2)];
            return (
              <circle
                key={`${category}-${index}`}
                className="appliance__mark"
                data-category={category}
                cx={index % 2 === 0 ? 34 : 62}
                cy={shelf - 9}
                r="5.5"
              />
            );
          })}
        </g>

        {/* The door, hinged on the left. */}
        <g className="appliance__door">
          <rect x="8" y="8" width="84" height="154" rx="9" />
          {place === "pantry" ? (
            // A cupboard reads as a cupboard because of the centre seam.
            <line x1="50" y1="8" x2="50" y2="162" />
          ) : (
            <line x1="78" y1="60" x2="78" y2="110" strokeWidth="3" />
          )}
          {place === "freezer" && (
            <g className="appliance__glyph">
              <line x1="50" y1="30" x2="50" y2="46" />
              <line x1="43" y1="34" x2="57" y2="42" />
              <line x1="57" y1="34" x2="43" y2="42" />
            </g>
          )}
        </g>
      </svg>

      <span className="appliance__label">{LABELS[place]}</span>
      <span className="appliance__count">{count}</span>
    </button>
  );
}
